import React, { createContext, useContext, useMemo, useState } from 'react'

/**
 * Usage: const { current, clear } = useFlash()
 *   const { show } = useFlash()
 *   show('success', 'Erfolgreich gespeichert')
 */
export type FlashKind = 'success' | 'error' | 'info'
export type FlashPayload = { type: FlashKind; message: string } | null

type Ctx = {
    current: FlashPayload
    show: (type: FlashKind, message: string) => void
    clear: () => void
}

const FlashContext = createContext<Ctx | undefined>(undefined)

export function FlashProvider({ children }: { children: React.ReactNode }) {
    const [current, setCurrent] = useState<FlashPayload>(null)
    const value = useMemo<Ctx>(
        () => ({
            current,
            show: (type, message) => setCurrent({ type, message }),
            clear: () => setCurrent(null),
        }),
        [current]
    )

    return <FlashContext.Provider value={value}>{children}</FlashContext.Provider>
}

export function useFlash() {
    const ctx = useContext(FlashContext)
    if (!ctx) throw new Error('useFlash must be used within <FlashProvider>')
    return ctx
}
